import { useUpdate, useCreate, useNotify, useRefresh } from "ra-core";
import { useCallback, useState } from "react";

interface Estimate {
  id: string;
  contact_id: string | number;
  estimate_number?: string;
  status: string;
  title: string;
}

/**
 * Hook providing status-transition actions for estimates.
 * Each action updates the estimate AND creates a timeline event.
 */
export function useEstimateActions() {
  const [update] = useUpdate();
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isPending, setIsPending] = useState(false);

  const transition = useCallback(
    async (
      estimate: Estimate,
      toStatus: string,
      eventType: string,
      timestampField: string
    ) => {
      setIsPending(true);
      const now = new Date().toISOString();
      try {
        // 1. Update estimate status
        await update("estimates", {
          id: estimate.id,
          data: {
            status: toStatus,
            [timestampField]: now,
          },
          previousData: { id: estimate.id },
        });

        // 2. Create timeline event
        await create("events", {
          data: {
            contact_id: estimate.contact_id,
            event_type: eventType,
            payload: {
              estimate_id: estimate.id,
              estimate_number: estimate.estimate_number,
              title: estimate.title,
              from_status: estimate.status,
              to_status: toStatus,
              timestamp: now,
            },
          },
        });

        notify(
          `Estimate ${estimate.estimate_number || ""} → ${toStatus}`,
          { type: "success" }
        );
        refresh();
      } catch (err) {
        notify(`Action failed: ${(err as Error).message}`, { type: "error" });
      } finally {
        setIsPending(false);
      }
    },
    [update, create, notify, refresh]
  );

  const send = useCallback(
    (est: Estimate) => transition(est, "sent", "estimate_sent", "sent_at"),
    [transition]
  );

  const accept = useCallback(
    (est: Estimate) =>
      transition(est, "accepted", "estimate_accepted", "accepted_at"),
    [transition]
  );

  const decline = useCallback(
    (est: Estimate) =>
      transition(est, "declined", "estimate_declined", "declined_at"),
    [transition]
  );

  return { send, accept, decline, isPending };
}
